/**
 * API endpoints para Warehouses (Bodegas)
 * Base URL: /api/inventory/
 */

import { GET, POST, PUT, PATCH, DELETE } from "../config";

/**
 * Listar bodegas
 * @param {number} page - Número de página
 * @param {object} filters - Filtros (branch, is_active, search)
 * @returns {Promise} Lista de bodegas
 */
export const list = async (page = 1, filters = {}) => {
  let url = `inventory/warehouses/?page=${page}&page_size=50`;

  if (filters.branch) {
    url += `&branch=${filters.branch}`;
  }

  if (filters.is_active !== undefined) {
    url += `&is_active=${filters.is_active}`;
  }

  if (filters.search) {
    url += `&name__icontains=${encodeURIComponent(filters.search)}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener una bodega por ID
 * @param {string|number} id - ID de la bodega
 * @returns {Promise} Datos de la bodega
 */
export const get = async (id) => {
  const response = await GET(`inventory/warehouses/${id}/`);
  return response.data;
};

/**
 * Crear una bodega
 * @param {object} data - Datos de la bodega
 * @returns {Promise} Bodega creada
 */
export const create = async (data) => {
  const response = await POST("inventory/warehouses/", data);
  return response.data;
};

/**
 * Actualizar una bodega
 * @param {string|number} id - ID de la bodega
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Bodega actualizada
 */
export const update = async (id, data) => {
  const response = await PUT(`inventory/warehouses/${id}/`, data);
  return response.data;
};

/**
 * Actualizar parcialmente una bodega
 * @param {string|number} id - ID de la bodega
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Bodega actualizada
 */
export const partialUpdate = async (id, data) => {
  const response = await PATCH(`inventory/warehouses/${id}/`, data);
  return response.data;
};

/**
 * Eliminar una bodega
 * @param {string|number} id - ID de la bodega
 * @returns {Promise}
 */
export const remove = async (id) => {
  const response = await DELETE(`inventory/warehouses/${id}/`);
  return response.data;
};

/**
 * Obtener productos de una bodega
 * @param {string|number} warehouseId - ID de la bodega
 * @param {object} params - Parámetros adicionales
 * @returns {Promise} Lista de productos en la bodega
 */
export const getProducts = async (warehouseId, params = {}) => {
  let url = `inventory/warehouse-products/?warehouse=${warehouseId}`;
  
  if (params.page) {
    url += `&page=${params.page}`;
  }
  
  if (params.search) {
    url += `&search=${encodeURIComponent(params.search)}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Agregar producto a bodega
 * @param {object} data - Datos del producto en bodega
 * @returns {Promise} Producto agregado
 */
export const addProduct = async (data) => {
  const response = await POST("inventory/warehouse-products/", data);
  return response.data;
};

/**
 * Actualizar stock de un producto en bodega
 * @param {string|number} id - ID del registro warehouse-product
 * @param {object} data - Datos a actualizar (quantity, min_stock, etc.)
 * @returns {Promise} Producto actualizado
 */
export const updateProductStock = async (id, data) => {
  const response = await PATCH(`inventory/warehouse-products/${id}/`, data);
  return response.data;
};

/**
 * Transferir productos entre bodegas
 * @param {object} data - Datos de la transferencia
 * @param {number} data.source_warehouse - ID bodega origen
 * @param {number} data.target_warehouse - ID bodega destino
 * @param {number} data.product - ID producto
 * @param {number} data.quantity - Cantidad a transferir
 * @returns {Promise} Resultado de la transferencia
 */
export const transferProducts = async (data) => {
  const response = await POST("inventory/warehouse-products/transfer/", data);
  return response.data;
};

/**
 * Obtener movimientos de stock de una bodega
 * @param {string|number} warehouseId - ID de la bodega
 * @param {object} params - Filtros (product, movement_type, date_from, date_to)
 * @returns {Promise} Lista de movimientos
 */
export const getStockMovements = async (warehouseId, params = {}) => {
  let url = `inventory/inventory-history/?warehouse=${warehouseId}`;
  
  if (params.product) {
    url += `&product=${params.product}`;
  }
  
  if (params.movement_type) {
    url += `&movement_type=${params.movement_type}`;
  }
  
  if (params.date_from) {
    url += `&created__gte=${params.date_from}`;
  }
  
  if (params.date_to) {
    url += `&created__lte=${params.date_to}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener resumen de stock por bodega
 * @param {number} branchId - ID de la sucursal
 * @returns {Promise} Resumen de stock
 */
export const getStockSummary = async (branchId) => {
  const url = branchId 
    ? `inventory/warehouses/stock_summary/?branch=${branchId}`
    : "inventory/warehouses/stock_summary/";
  
  const response = await GET(url);
  return response.data;
};

/**
 * Ajustar stock manualmente
 * @param {object} data - Datos del ajuste
 * @returns {Promise} Resultado del ajuste
 */
export const adjustStock = async (data) => {
  const response = await POST("inventory/inventory-history/", {
    ...data,
    movement_type: "adjustment",
  });
  return response.data;
};

// ==================== EXPORT ====================

const warehousesApi = {
  list,
  get,
  create,
  update,
  partialUpdate,
  remove,
  getProducts,
  addProduct,
  updateProductStock,
  transferProducts,
  getStockMovements,
  getStockSummary,
  adjustStock,
};

export default warehousesApi;
